import { ref, computed } from 'vue'

// Shared, module-level store for GET /api/services/duration-and-prices.
// Consumed by ServicesSection (duration chips), the sticky CTA bar and the booking sheet.

const services = ref([])
const isLoading = ref(false)
const isLoaded = ref(false)
const error = ref(null)
let inflight = null

const load = () => {
    if (isLoaded.value) return Promise.resolve(services.value)
    if (inflight) return inflight

    isLoading.value = true
    inflight = (async () => {
        try {
            const response = await fetch('/api/services/duration-and-prices')
            if (!response.ok) throw new Error(`Services API failed: ${response.status}`)
            const data = await response.json()

            services.value = (data.data || [])
                .filter((service) => !String(service.name || '').includes('System Break'))
                .map((service) => ({
                    id: service.id,
                    duration: service.duration,
                    price: parseFloat(service.price),
                    featured: service.featured,
                    name: service.name
                }))
                .sort((a, b) => a.duration - b.duration)

            isLoaded.value = true
            error.value = null
        } catch (err) {
            error.value = err
            console.error('[Services]', err.message)
        } finally {
            isLoading.value = false
            inflight = null
        }
        return services.value
    })()

    return inflight
}

// Explicit retry after a failed load (the inline error banner uses this).
const reload = () => {
    isLoaded.value = false
    inflight = null
    return load()
}

const featuredService = computed(() => services.value.find((s) => s.featured) || null)

const cheapestService = computed(() => {
    if (services.value.length === 0) return null
    return services.value.reduce((min, s) => (s.price < min.price ? s : min), services.value[0])
})

const findById = (id) => (id ? services.value.find((s) => s.id === id) || null : null)

export const useServices = () => ({
    services,
    isLoading,
    isLoaded,
    error,
    load,
    reload,
    featuredService,
    cheapestService,
    findById
})
